<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="points-settings-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Punkty</h1>
      </div>
    </div>
    <!-- Points Configuration -->
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-1 mb-6 content-card">
          <div class="table-section-title">
            <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-star-circle</v-icon></div>
            <div class="filter-title">{{ $t('pointsSettings.pointRates') }}</div>
          </div>
          <v-card-text>
            <v-form ref="configForm" v-model="configValid">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="config.earnings_per_point_zl"
                    label="Zarobek na 1 punkt (PLN)"
                    type="number"
                    outlined
                    :rules="[rules.required, rules.positive]"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="config.referral_points"
                    label="Punkty za polecenie"
                    type="number"
                    outlined
                    :rules="[rules.required, rules.positive]"
                  />
                </v-col>
                <v-col cols="12">
                  <div class="text-subtitle-2 font-weight-bold mb-2">{{ $t('pointsSettings.referralConditions') }}</div>
                  <div class="text-caption grey--text mb-2">Kazdy spelniony warunek przyznaje osobno punkty za polecenie. Pusta lista = punkty przyznawane raz przy rejestracji.</div>
                  <v-card v-for="(cond, idx) in referralConditions" :key="idx" outlined class="mb-2 pa-3">
                    <v-row dense align="center">
                      <v-col cols="12" md="3">
                        <v-select
                          v-model="cond.type"
                          :items="referralConditionOptions"
                          label="Typ warunku"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model.number="cond.points"
                          label="Punkty"
                          type="number"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="2" v-if="cond.type === 'activity_days'">
                        <v-text-field
                          v-model.number="cond.value"
                          label="Liczba dni"
                          type="number"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="2" v-if="cond.type === 'gross_turnover'">
                        <v-text-field
                          v-model.number="cond.value"
                          label="Obrót brutto (zł)"
                          type="number"
                          dense
                          outlined
                          hide-details
                        />
                      </v-col>
                      <v-col cols="auto">
                        <v-btn icon small color="error" @click="removeCondition(idx)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                  <v-btn small text color="primary" @click="addCondition" class="mt-1">
                    <v-icon small left>mdi-plus</v-icon>{{ $t('pointsSettings.addCondition') }}
                  </v-btn>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="config.review_points_per_portal"
                    label="Punkty za opinie (za portal)"
                    type="number"
                    outlined
                    :rules="[rules.required, rules.positive]"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="config.ranking_top_bonus"
                    label="Bonus za TOP w rankingu"
                    type="number"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" :disabled="!configValid" :loading="configSaving" @click="saveConfig">
              {{ $t('pointsSettings.saveConfig') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Rewards Catalog -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="catalogHeaders"
          :items="catalogItems"
          class="elevation-1 mb-6"
          :loading="catalogLoading"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-gift-outline</v-icon></div>
              <div class="filter-title">{{ $t('pointsSettings.rewardCatalog') }}</div>
              <v-spacer></v-spacer>
              <button class="action-pill action-pill--primary" @click="openCatalogDialog(null)">
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('common.add') }}
              </button>
            </div>
          </template>
          <template v-slot:item.available="{ item }">
            <v-chip :color="item.available ? 'green' : 'red'" dark small>
              {{ item.available ? 'Tak' : 'Nie' }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn class="warning mr-2" small @click="openCatalogDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Regulation Upload -->
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-1 content-card">
          <div class="table-section-title">
            <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-file-document-outline</v-icon></div>
            <div class="filter-title">{{ $t('pointsSettings.regulation') }}</div>
          </div>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <p v-if="regulation.version">
                  <strong>Aktualna wersja:</strong> {{ regulation.version }}
                </p>
                <p v-if="regulation.uploadedAt">
                  <strong>Data uploadu:</strong> {{ regulation.uploadedAt }}
                </p>
                <p v-if="!regulation.version" class="grey--text">{{ $t('pointsSettings.noRegulation') }}</p>
                <v-btn
                  v-if="regulation.version"
                  color="primary"
                  outlined
                  small
                  :loading="regulationPreviewLoading"
                  @click="previewRegulation"
                >
                  <v-icon small left>mdi-file-eye-outline</v-icon>
                  Podgląd aktualnego regulaminu
                </v-btn>
              </v-col>
              <v-col cols="12" md="6">
                <v-file-input
                  v-model="regulationFile"
                  label="Nowy regulamin (PDF)"
                  accept=".pdf"
                  outlined
                />
                <v-alert v-if="regulationFile" type="warning" dense class="mb-2">
                  Upload nowego regulaminu wymusi ponowna akceptacje przez wszystkich kierowcow.
                </v-alert>
                <v-btn
                  color="primary"
                  :disabled="!regulationFile"
                  :loading="regulationUploading"
                  @click="uploadRegulation"
                >
                  {{ $t('pointsSettings.uploadRegulation') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Catalog Add/Edit Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="catalogDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ catalogEditId ? $t('pointsSettings.editReward') : $t('pointsSettings.newReward') }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="catalogForm" v-model="catalogFormValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="catalogForm.name"
                  label="Nazwa"
                  outlined
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="catalogForm.type"
                  :items="rewardTypeOptions"
                  label="Typ"
                  outlined
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="catalogForm.costPoints"
                  label="Koszt (punkty)"
                  type="number"
                  outlined
                  :rules="[rules.required, rules.positive]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="catalogForm.valueZl"
                  label="Wartosc (PLN)"
                  type="number"
                  outlined
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="catalogForm.monthlyLimit"
                  label="Limit miesieczny"
                  type="number"
                  outlined
                />
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="catalogForm.available"
                  label="Dostepna"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="secondary" @click="catalogDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :disabled="!catalogFormValid" @click="saveCatalogItem">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");
export default {
  data() {
    return {
      // Config
      config: {
        earnings_per_point_zl: null,
        referral_points: null,
        review_points_per_portal: null,
        ranking_top_bonus: null,
      },
      referralConditions: [
        { type: "registration", value: "", points: 200 },
      ],
      configValid: false,
      configSaving: false,
      referralConditionOptions: [
        { text: "Rejestracja", value: "registration" },
        { text: "Dni aktywnosci", value: "activity_days" },
        { text: "Pierwszy kurs", value: "first_ride" },
        { text: "Zapis do programu punktowego", value: "enrolled" },
        { text: "Obrót brutto (zł)", value: "gross_turnover" },
      ],
      // Catalog
      catalogLoading: false,
      catalogItems: [],
      catalogHeaders: [
        { text: "Nazwa", value: "name", sortable: false },
        { text: "Typ", value: "type", sortable: false },
        { text: "Koszt (pkt)", value: "costPoints", sortable: false },
        { text: "Wartosc (PLN)", value: "valueZl", sortable: false },
        { text: "Limit mies.", value: "monthlyLimit", sortable: false },
        { text: "Dostepna", value: "available", sortable: false },
        { text: "Akcje", value: "actions", sortable: false },
      ],
      catalogDialog: false,
      catalogFormValid: false,
      catalogEditId: null,
      catalogForm: {
        name: "",
        type: "",
        costPoints: null,
        valueZl: null,
        monthlyLimit: null,
        available: true,
      },
      rewardTypeOptions: [
        { text: "Voucher", value: "VOUCHER" },
        { text: "Voucher gotówkowy", value: "CASH_VOUCHER" },
        { text: "Nagroda rzeczowa", value: "PHYSICAL" },
      ],
      // Regulation
      regulation: {
        version: null,
        uploadedAt: null,
        fileUrl: null,
      },
      regulationFile: null,
      regulationUploading: false,
      regulationPreviewLoading: false,
      // Rules
      rules: {
        required: (v) => !!v || v === 0 || "Wymagane",
        positive: (v) => v >= 0 || "Wartosc musi byc dodatnia",
      },
    };
  },
  async created() {
    await Promise.all([
      this.loadConfig(),
      this.loadCatalog(),
      this.loadRegulation(),
    ]);
  },
  methods: {
    async toggleLoading(type) {
      localStorage.setItem("loading", type);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    getAuthHeaders() {
      return {
        Authorization: "Bearer " + localStorage.getItem("token"),
      };
    },
    addCondition() {
      this.referralConditions.push({ type: "registration", value: "", points: 200 });
    },
    removeCondition(idx) {
      this.referralConditions.splice(idx, 1);
    },
    async loadConfig() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "points/admin/config",
          headers: this.getAuthHeaders(),
        });
        const data = res.data.data || res.data;
        if (Array.isArray(data)) {
          for (const item of data) {
            if (item.key in this.config) {
              const num = Number(item.value);
              this.config[item.key] = isNaN(num) ? item.value : num;
            }
            if (item.key === 'referral_conditions') {
              try {
                const parsed = JSON.parse(item.value);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  this.referralConditions = parsed;
                }
              } catch { /* keep default */ }
            }
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    async saveConfig() {
      if (!this.$refs.configForm.validate()) return;
      this.configSaving = true;
      try {
        const settings = Object.entries(this.config).map(([key, value]) => ({
          key,
          value: String(value),
        }));
        settings.push({
          key: 'referral_conditions',
          value: JSON.stringify(this.referralConditions),
        });
        await axios({
          method: "put",
          url: process.env.VUE_APP_ROOT_API + "points/admin/config",
          headers: this.getAuthHeaders(),
          data: { settings },
        });
        Swal.fire("Zapisano", "Konfiguracja zostala zapisana", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", e.response?.data?.message?.toString() || "Wystapil blad", "error");
      }
      this.configSaving = false;
    },
    async loadCatalog() {
      this.catalogLoading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "points/admin/rewards/catalog",
          headers: this.getAuthHeaders(),
        });
        this.catalogItems = res.data.data || res.data;
      } catch (e) {
        console.log(e);
      }
      this.catalogLoading = false;
    },
    openCatalogDialog(item) {
      if (item) {
        this.catalogEditId = item.id;
        this.catalogForm = {
          name: item.name,
          type: item.type,
          costPoints: item.costPoints,
          valueZl: item.valueZl,
          monthlyLimit: item.monthlyLimit,
          available: item.available,
        };
      } else {
        this.catalogEditId = null;
        this.catalogForm = {
          name: "",
          type: "",
          costPoints: null,
          valueZl: null,
          monthlyLimit: null,
          available: true,
        };
      }
      this.catalogDialog = true;
      this.$nextTick(() => {
        if (this.$refs.catalogForm) this.$refs.catalogForm.resetValidation();
      });
    },
    async saveCatalogItem() {
      if (!this.$refs.catalogForm.validate()) return;
      await this.toggleLoading(true);
      try {
        // Puste pola numeryczne z v-model.number to '' — backend (Prisma Int)
        // tego nie łyka. Normalizujemy przed wysyłką.
        const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
        const payload = {
          name: (this.catalogForm.name || '').trim(),
          type: this.catalogForm.type,
          costPoints: num(this.catalogForm.costPoints) || 0,
          valueZl: num(this.catalogForm.valueZl),
          monthlyLimit: num(this.catalogForm.monthlyLimit) || 0,
          available: this.catalogForm.available !== false,
        };
        if (this.catalogEditId) {
          await axios({
            method: "put",
            url: process.env.VUE_APP_ROOT_API + "points/admin/rewards/catalog/" + this.catalogEditId,
            headers: this.getAuthHeaders(),
            data: payload,
          });
        } else {
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "points/admin/rewards/catalog",
            headers: this.getAuthHeaders(),
            data: payload,
          });
        }
        this.catalogDialog = false;
        await this.loadCatalog();
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", e.response?.data?.message?.toString() || "Wystapil blad", "error");
      }
      await this.toggleLoading(false);
    },
    async loadRegulation() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "points/admin/regulation",
          headers: this.getAuthHeaders(),
        });
        const data = res.data.data || res.data;
        this.regulation.version = data.version || null;
        this.regulation.uploadedAt = data.uploadedAt || data.createdAt || null;
        this.regulation.fileUrl = data.fileUrl || null;
      } catch (e) {
        console.log(e);
      }
    },
    // fileUrl z bazy to klucz w MinIO, nie adres do otwarcia — PDF podaje
    // backend, tak samo jak przy regulaminie ogólnym w ustawieniach systemu.
    async previewRegulation() {
      this.regulationPreviewLoading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "points/admin/regulation/file",
          headers: this.getAuthHeaders(),
          responseType: "blob",
        });
        const blob = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(blob), "_blank");
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", "Nie udalo sie otworzyc regulaminu", "error");
      }
      this.regulationPreviewLoading = false;
    },
    async uploadRegulation() {
      if (!this.regulationFile) return;
      this.regulationUploading = true;
      try {
        const formData = new FormData();
        formData.append("file", this.regulationFile, this.regulationFile.name);
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "points/admin/regulation",
          headers: {
            ...this.getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        Swal.fire("Zapisano", "Regulamin zostal zaktualizowany", "success");
        this.regulationFile = null;
        await this.loadRegulation();
      } catch (e) {
        console.log(e);
        Swal.fire("Blad", e.response?.data?.message?.toString() || "Wystapil blad", "error");
      }
      this.regulationUploading = false;
    },
  },
};
</script>

<style scoped>
.points-settings-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.content-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px !important;
  border: 1px solid #2A3142 !important;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-pill--primary {
  background: #5478FF;
  color: #FFFFFF;
}
.action-pill--primary:hover {
  background: #3F62E8;
}
.points-settings-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.points-settings-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.points-settings-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.points-settings-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.points-settings-page >>> .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.points-settings-page >>> .v-card.v-sheet--outlined {
  border-color: #2A3142 !important;
}
.points-settings-page >>> .v-text-field input,
.points-settings-page >>> .v-text-field textarea,
.points-settings-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.points-settings-page >>> .v-label {
  color: #94A0B8 !important;
}
.points-settings-page >>> fieldset {
  border-color: #2A3142 !important;
}
.points-settings-page >>> .grey--text {
  color: #94A0B8 !important;
}
</style>
